/**
 * CareerTimeline — inline vertical timeline rendered within ProfileNotes.
 * Shows work history entries chronologically so the professional can
 * catch temporal/grouping errors during extraction.
 */

interface WorkGroup {
	company: string;
	title: string;
	period: string;
	status: 'pending' | 'approved' | 'mixed';
}

interface TimelineEntry {
	company: string;
	title: string;
	period: string;
	startYear: number | null;
	endYear: number | null;
	hasPending: boolean;
}

/** Parse a period string like "December 2021 - November 2025" into start/end years */
function parsePeriod(period: string): { start: number | null; end: number | null } {
	if (!period) return { start: null, end: null };
	const parts = period.split(/\s*[-–—]\s*/);
	const years = parts.map(p => {
		const match = p.match(/(\d{4})/);
		return match ? parseInt(match[1]) : null;
	});
	const end = years.length > 1 ? years[1] ?? null : null;
	// If start has no year but end does, inherit (e.g., "November - December 2013")
	const start = years[0] ?? end;
	return { start, end };
}

function formatDateLabel(period: string): { start: string; end: string } {
	if (!period) return { start: '?', end: '' };
	const { start, end } = parsePeriod(period);
	return {
		start: start ? String(start) : '?',
		end: end ? String(end) : (start ? '' : ''),
	};
}

export function CareerTimeline({ workGroups }: { workGroups: WorkGroup[] }) {
	if (workGroups.length === 0) return null;

	const entries: TimelineEntry[] = workGroups.map(group => {
		const { start, end } = parsePeriod(group.period);
		return {
			company: group.company,
			title: group.title,
			period: group.period,
			startYear: start,
			endYear: end,
			hasPending: group.status === 'pending' || group.status === 'mixed',
		};
	});

	// Compiler owns work history sort order — render in the order received.

	return (
		<div class="ct-inline">
			<div class="ct-track">
				<div class="ct-line" />
				{entries.map((entry, i) => {
					const dates = formatDateLabel(entry.period);
					return (
						<div
							key={`${entry.company}-${i}`}
							class={`ct-entry${entry.hasPending ? ' ct-entry--pending' : ''}`}
						>
							<div class="ct-date">
								<span class="ct-date-start">{dates.start}</span>
								{dates.end && <span class="ct-date-end">{dates.end}</span>}
							</div>
							<div class="ct-detail">
								<span class="ct-company">{entry.company}</span>
								{entry.title && <span class="ct-role">{entry.title}</span>}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}

export const careerTimelineStyles = `
	.ct-inline {
		padding: 0.5rem 0 0.25rem;
	}

	.ct-track {
		position: relative;
		padding: 0.25rem 0;
	}

	.ct-line {
		position: absolute;
		left: 4rem;
		top: 0.5rem;
		bottom: 0.5rem;
		width: 1px;
		background: var(--color-border);
	}

	.ct-entry {
		display: grid;
		grid-template-columns: 3.5rem 1fr;
		align-items: start;
		padding: 0.375rem 0 0.375rem 0.5rem;
		position: relative;
		animation: ct-fade-in 300ms ease-out;
	}

	@keyframes ct-fade-in {
		from { opacity: 0; transform: translateX(-6px); }
		to { opacity: 1; transform: translateX(0); }
	}

	.ct-date {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		padding-right: 0.5rem;
		padding-top: 1px;
	}

	.ct-date-start {
		font-family: var(--font-mono);
		font-size: 0.625rem;
		font-weight: 500;
		color: var(--color-text-muted);
		line-height: 1;
	}

	.ct-date-end {
		font-family: var(--font-mono);
		font-size: 0.5625rem;
		color: var(--color-text-subtle);
		margin-top: 2px;
		line-height: 1;
	}


	.ct-detail {
		display: flex;
		flex-direction: column;
		padding-left: 0.5rem;
		min-width: 0;
	}

	.ct-company {
		font-size: 0.75rem;
		font-weight: 500;
		color: var(--color-text);
		line-height: 1.2;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.ct-role {
		font-size: 0.625rem;
		color: var(--color-text-subtle);
		margin-top: 1px;
		line-height: 1.3;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.ct-entry--pending .ct-company {
		color: var(--color-primary);
	}

	.ct-entry--pending .ct-date-start {
		color: var(--color-primary);
	}
`;
